import {DataStatus, Flag, Label, Parameters, Rank, Tag, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {ReactElement} from 'react';

import {isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {
  buildArray,
  EMPTY_PLACEHOLDER,
  isNotNilOrEmpty,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {AuctionTypeFilter} from '../../pages/AllOffers/types/AuctionTypeFilter';
import {AuctionSourcingListItem} from '../../utils/adaptAuctionVehicleToSourcingItem';
import {buildAuctionVehicleTitle} from '../../utils/buildAuctionVehicleTitle';
import {resolveAuctionEnumLabel} from '../../utils/resolveAuctionEnumLabel';
import {ActionButtonsVehicle} from '../ActionButtonsVehicle/ActionButtonsVehicle';
import {Car} from '../AdvanceVehicleView/components/Car';
import {AuctionCountdownCell} from '../AuctionPreview/components/AuctionCountdownCell';
import {Clamp} from '../Clamp/Clamp';
import {CountryFlag} from '../CountryFlag/CountryFlag';
import {HitlistBlacklistFlag} from '../HitlistBlacklistFlag/HitlistBlacklistFlag';
import {createRowElementId} from '../SourcingTable/utils/createRowElementId';
import {convertVehicleTagsToVehicleHighlights} from '../VehicleHighlights/utils/convertVehicleTagsToVehicleHighlights';
import {VehicleHighlights} from '../VehicleHighlights/VehicleHighlights';
import {VehicleImage} from '../VehicleImage/VehicleImage';

export interface AdvanceAuctionViewProps extends TestIdProps {
  index?: number;
  vehicle?: AuctionSourcingListItem;
  isVehicleLoading?: boolean;
  onRowClick?: () => void;
  isLastClickedRow?: boolean;
  setLastVisitedVehicle?: (adId: string, index: number) => void;
  children?: ReactElement;
}

const PERCENTAGE_MULTIPLIER = 100;

export function AdvanceAuctionView(props: AdvanceAuctionViewProps) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const enumLookup = useAuctionEnumLookup();
  const sourcingVehicle =
    props.vehicle && 'sourcingVehicle' in props.vehicle ? props.vehicle.sourcingVehicle : null;
  const auctionVehicle = props.vehicle?.auctionVehicle;
  const suspiciousReasons = sourcingVehicle?.suspiciousReason
    ?.map((reason) => i18n.t(`entity.vehicle.labels.${reason}`))
    .join(', ');
  const firstLine = buildAuctionVehicleTitle(
    auctionVehicle?.make ?? null,
    auctionVehicle?.model ?? null,
    auctionVehicle?.adTitle ?? null,
    enumLookup
  );
  const secondLineParams = buildArray<string>()
    .when(isNotNilOrEmpty(auctionVehicle?.seller?.name), auctionVehicle?.seller?.name ?? '')
    .when(isNotNilOrEmpty(auctionVehicle?.year), String(auctionVehicle?.year ?? ''))
    .when((auctionVehicle?.power ?? 0) > 0, `${auctionVehicle?.power} kW`)
    .when(
      isNotNilOrEmpty(auctionVehicle?.transmission),
      resolveAuctionEnumLabel(auctionVehicle?.transmission ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.driveType),
      resolveAuctionEnumLabel(auctionVehicle?.driveType ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.fuelType),
      resolveAuctionEnumLabel(auctionVehicle?.fuelType ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.carStyle),
      resolveAuctionEnumLabel(auctionVehicle?.carStyle ?? null, enumLookup) ?? ''
    )
    .when(
      isNotNilOrEmpty(auctionVehicle?.mileage),
      `${formatNumber(auctionVehicle?.mileage ?? 0, 0)} km`
    );

  const marketPosition = auctionVehicle?.marketPosition;
  const marketPositionCount = auctionVehicle?.marketPositionCount;
  const auctionCurrency = auctionVehicle?.currency;
  const formattedCurrentBid =
    (auctionVehicle?.priceActual ?? 0) > 0
      ? (formatCurrency(auctionVehicle?.priceActual ?? 0, auctionCurrency ?? '', 0) ??
        EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;
  const buyNowPrice = auctionVehicle?.priceBuyNow;
  const buyNowCurrency = auctionVehicle?.currency;
  const auctionType = auctionVehicle?.auctionType?.toLowerCase();
  const isBuyNowType = auctionType === AuctionTypeFilter.BUY_NOW;
  const isOpenType = auctionType === AuctionTypeFilter.OPEN;
  const isClosedType = auctionType === AuctionTypeFilter.CLOSED;
  const isBuyNowPriceAvailable = isBuyNowType && isNotNil(buyNowPrice) && buyNowPrice > 0;
  const formattedBuyNowPrice = isNotNilOrEmpty(buyNowCurrency)
    ? formatCurrency(buyNowPrice ?? 0, buyNowCurrency, 0)
    : formatNumber(buyNowPrice ?? 0, 0);
  const formattedMargin = isNotNil(auctionVehicle?.maximalPossibleMargin)
    ? (formatCurrency(auctionVehicle?.maximalPossibleMargin ?? 0, auctionCurrency ?? '', 0) ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;
  const formattedRecommendedPrice = isNotNil(auctionVehicle?.predictedPrice)
    ? (formatCurrency(auctionVehicle?.predictedPrice ?? 0, auctionCurrency ?? '', 0) ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;
  const positionText =
    isNotNil(marketPosition) && isNotNil(marketPositionCount)
      ? `${marketPosition}/${marketPositionCount}`
      : EMPTY_PLACEHOLDER;
  const positionPercentage =
    isNotNil(marketPosition) && isNotNil(marketPositionCount) && marketPositionCount > 0
      ? Math.round(((marketPositionCount - marketPosition) / marketPositionCount) * 100)
      : null;
  const percentText = isNotNil(positionPercentage) ? `${positionPercentage}%` : undefined;

  return (
    <Car
      id={props.vehicle && createRowElementId(props.vehicle)}
      key={sourcingVehicle?.adId}
      // Legacy component
      // eslint-disable-next-line react/forbid-component-props
      className={props.isLastClickedRow ? 'active-iframe' : ''}
      isNew={props.vehicle?.isNew}
      isDisabled={sourcingVehicle?.isAvailable === false}
      onClick={props.onRowClick}
      isLoading={!sourcingVehicle}
    >
      <VehicleImage vehicle={props.vehicle} borderRadius="medium" />
      <DataStatus isLoading={props.isVehicleLoading}>
        <Box padding={3}>
          <VStack spacing={2}>
            <HStack align="center" spacing={2} wrap="nowrap">
              <CountryFlag country={sourcingVehicle?.seller?.country?.key} />
              <Show when={sourcingVehicle?.isAvailable === false}>
                <Flag
                  label={i18n.t('entity.vehicle.labels.sold')}
                  size="small"
                  data-testid={suffixTestId('sold', props)}
                />
              </Show>
              <Show when={props.vehicle?.isNew}>
                <Box
                  backgroundColor="palettes.green.60.100"
                  width={3}
                  height={3}
                  borderRadius="circular"
                />
              </Show>
              <HStack align="center" spacing={2} grow={1}>
                <Tooltip label={firstLine}>
                  <Clamp lines={1}>
                    <Heading size={4} data-testid={suffixTestId('vehicleName', props)}>
                      {firstLine}
                    </Heading>
                  </Clamp>
                </Tooltip>
                <Show when={props.vehicle?.vehicleSummary?.comment}>
                  <Tooltip placement="top" label={props.vehicle?.vehicleSummary?.comment}>
                    <Icon value="communication/comment" />
                  </Tooltip>
                </Show>
                <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                  <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
                </Show>
                <Show
                  whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                  when={sourcingVehicle?.suspicious}
                >
                  <Tooltip label={suspiciousReasons}>
                    <Icon value="alert/warning" color="severity.warning" size={5} />
                  </Tooltip>
                </Show>
              </HStack>
              {props.vehicle && sourcingVehicle?.isAvailable && (
                <ActionButtonsVehicle
                  vehicle={props.vehicle}
                  onDetailOpen={() =>
                    props.setLastVisitedVehicle?.(auctionVehicle?.adId ?? '', props.index ?? 0)
                  }
                  data-testid={suffixTestId('actionButtonsVehicle', props)}
                />
              )}
            </HStack>
            <Show when={isNotNil(sourcingVehicle)}>
              <Parameters
                size="small"
                color="secondary"
                parameters={secondLineParams}
                data-testid={suffixTestId('vehicleParameters', props)}
              />
            </Show>
            <VehicleHighlights
              data={convertVehicleTagsToVehicleHighlights(
                [],
                [],
                Object.values(props.vehicle?.features ?? {}).filter((item) => item.isHighlight)
              )}
            />
            <HStack justify="space-between" spacing={2} wrap>
              <VStack align="flex-start">
                <Label>{i18n.t('entity.auction.labels.currentBid')}</Label>
                <Text size="xSmall">{formattedCurrentBid}</Text>
                <Show when={isBuyNowPriceAvailable}>
                  <Space vertical={1} />
                  <Tooltip
                    label={i18n.t('entity.auction.labels.buyNowForPrice', {
                      price: formattedBuyNowPrice,
                    })}
                  >
                    <Box
                      backgroundColor="severity.success"
                      borderRadius="xSmall"
                      paddingHorizontal={1}
                      data-testid={suffixTestId('currentBid-buyNowFlag', props)}
                    >
                      <HStack spacing={1} align="center">
                        <Icon value="action/shopping_cart" color="general.white" size={3} />
                        <Text size="xxSmall" color="white" noWrap>
                          {formattedBuyNowPrice}
                        </Text>
                      </HStack>
                    </Box>
                  </Tooltip>
                </Show>
                <Show when={isOpenType}>
                  <Space vertical={1} />
                  <Tag
                    size="small"
                    colorScheme="blue"
                    label={i18n.t('entity.auction.labels.open')}
                    data-testid={suffixTestId('currentBid-openFlag', props)}
                  />
                </Show>
                <Show when={isClosedType}>
                  <Space vertical={1} />
                  <Tag
                    size="small"
                    colorScheme="neutral"
                    label={i18n.t('entity.auction.labels.closed')}
                    data-testid={suffixTestId('currentBid-closedFlag', props)}
                  />
                </Show>
              </VStack>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.margin')}</Label>
                <Text
                  size="xSmall"
                  color={
                    isNotNil(auctionVehicle?.maximalPossibleMargin) &&
                    auctionVehicle.maximalPossibleMargin < 0
                      ? 'danger'
                      : undefined
                  }
                >
                  {formattedMargin}
                </Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.recommendedPrice')}</Label>
                <Text size="xSmall">{formattedRecommendedPrice}</Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.position')}</Label>
                <Text size="xSmall">{positionText}</Text>
                <Show when={isNotNil(percentText)}>
                  <Space vertical={1} />
                  <Text size="xxSmall" color="tertiary">
                    {percentText}
                  </Text>
                </Show>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.LID')}</Label>
                <Text size="xSmall">
                  {isNotNil(auctionVehicle?.turnover)
                    ? formatNumber(auctionVehicle.turnover, 0)
                    : EMPTY_PLACEHOLDER}
                </Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.features')}</Label>
                <Rank
                  percentage={Math.floor(
                    (auctionVehicle?.featureScore ?? 0) * PERCENTAGE_MULTIPLIER
                  )}
                  data-testid={suffixTestId('rank', props)}
                />
              </Box>
              <Box>
                <Label>{i18n.t('entity.auction.labels.auctionEnd')}</Label>
                <Text size="xSmall">
                  <AuctionCountdownCell endTime={auctionVehicle?.carEndTime ?? ''} />
                </Text>
              </Box>
            </HStack>
          </VStack>
        </Box>
      </DataStatus>
    </Car>
  );
}
